/**
 * Estado de sesion de la aplicacion.
 *
 * Guarda quien es el usuario, en que tenant esta y con que company trabaja. No
 * guarda datos de negocio: eso es responsabilidad de TanStack Query, que sabe
 * invalidar y refetchear. Aca solo vive lo que cambia poco y condiciona todo.
 *
 * Los tokens NO se duplican en el store: la fuente de verdad es `lib/api`. El
 * store expone `isAuthenticated` como reflejo, para que la UI reaccione.
 */

import { create } from 'zustand';

import { ApiError, api } from '@/lib/api';
import { claveGuardada, guardarMeta, leerMetaGuardada, olvidarMetaGuardada } from '@/lib/metaGuardada';
import type { CompanyRef, Session, SessionUser, TenantRef } from '@/lib/types';

/**
 * - `unknown`: todavia no se pregunto al backend (primer render).
 * - `loading`: hay una peticion de sesion en vuelo.
 * - `authenticated`: sesion valida y datos cargados.
 * - `anonymous`: sin token o con token rechazado. Toca login.
 * - `error`: el backend fallo por algo que no es autenticacion (red, 500).
 */
export type SessionStatus = 'unknown' | 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
  tenant: TenantRef | null;
  companies: CompanyRef[];
  activeCompanyId: number | null;
  modules: string[];
  /** Mensaje del ultimo fallo, para mostrarlo en el login o en la pantalla de error. */
  error: string | null;
  /** `true` mientras corre un login, para deshabilitar el boton sin estado local. */
  signingIn: boolean;

  /** Arranca la app: si hay token, pide la sesion. Idempotente. */
  bootstrap: () => Promise<void>;
  signIn: (username: string, password: string) => Promise<boolean>;
  signOut: () => void;
  /** Cambia la company activa. Devuelve `false` si el id no esta habilitado. */
  setActiveCompany: (companyId: number) => Promise<boolean>;
  /** Vuelve a pedir `/session/` sin tocar el estado de autenticacion. */
  reload: () => Promise<void>;
}

function aplicarSesion(sesion: Session): Partial<SessionState> {
  // El backend manda la company activa que efectivamente resolvio. Se persiste en
  // el cliente para que la cabecera `X-Company-Id` sobreviva a una recarga.
  api.setActiveCompanyId(sesion.active_company);

  return {
    status: 'authenticated',
    user: sesion.user,
    tenant: sesion.tenant,
    companies: sesion.companies,
    activeCompanyId: sesion.active_company,
    modules: sesion.modules,
    error: null,
  };
}

// ---------------------------------------------------------------------------
// La última sesión, guardada en el navegador
// ---------------------------------------------------------------------------
/**
 * POR QUÉ, medido con la demora real de producción: en cada recarga de la
 * página (F5, una pestaña nueva, volver al sistema) no se dibujaba NADA hasta
 * que `/session/` contestaba —alrededor de un segundo— y recién entonces se
 * pedía lo de la pantalla. Con la última sesión guardada, la pantalla se dibuja
 * al instante con ella y la sesión se confirma de fondo; lo que cambió (otra
 * empresa, un módulo nuevo) se aplica cuando llega.
 *
 * NO ES UNA SESIÓN: es lo que se DIBUJA mientras se confirma la de verdad. El
 * token lo sigue validando el servidor en cada pedido. Si la confirmación dice
 * que el token ya no vale —venció, se cortó la cuenta, se suspendió la
 * organización—, se borra todo y se vuelve al ingreso, igual que antes. Se
 * guarda por usuario y por versión del sistema (ver `lib/metaGuardada.ts`), y
 * salir lo borra.
 */
const CLAVE_DE_SESION = '__sesion__';

function sesionGuardada(): Session | undefined {
  return leerMetaGuardada<Session>(claveGuardada(null, CLAVE_DE_SESION))?.spec;
}

function guardarSesion(sesion: Session): void {
  guardarMeta(claveGuardada(null, CLAVE_DE_SESION), sesion);
}

/** La confirmación de fondo en vuelo, para no pedirla dos veces si `bootstrap` se repite. */
let confirmando: Promise<void> | null = null;

const ESTADO_ANONIMO = {
  status: 'anonymous' as const,
  user: null,
  tenant: null,
  companies: [] as CompanyRef[],
  activeCompanyId: null,
  modules: [] as string[],
};

export const useSessionStore = create<SessionState>()((set, get) => ({
  status: 'unknown',
  user: null,
  tenant: null,
  companies: [],
  activeCompanyId: api.getActiveCompanyId(),
  modules: [],
  error: null,
  signingIn: false,

  bootstrap: async () => {
    if (!api.isAuthenticated()) {
      set({ ...ESTADO_ANONIMO, error: null });
      return;
    }
    if (get().status === 'loading' || confirmando !== null) return;

    // Con la última sesión guardada, la pantalla se dibuja YA y se confirma de
    // fondo. Sin ella, como siempre: se espera la sesión.
    const guardada = sesionGuardada();
    set(guardada === undefined ? { status: 'loading', error: null } : aplicarSesion(guardada));
    confirmando = (async () => {
      try {
        const sesion = await api.fetchSession();
        guardarSesion(sesion);
        set(aplicarSesion(sesion));
      } catch (error) {
        if (error instanceof ApiError && error.isAuthError) {
          api.clearSession();
          olvidarMetaGuardada();
          set({ ...ESTADO_ANONIMO, error: null });
          return;
        }
        // Un corte de red con la sesión ya dibujada no echa a nadie: se sigue
        // con la guardada y cada pedido vuelve a probar. Sin sesión guardada
        // no hay qué dibujar, y es la pantalla de error de siempre.
        if (guardada !== undefined) return;
        set({
          status: 'error',
          error: error instanceof Error ? error.message : 'No se pudo cargar la sesión.',
        });
      } finally {
        confirmando = null;
      }
    })();
    await confirmando;
  },

  signIn: async (username, password) => {
    set({ signingIn: true, error: null });
    try {
      await api.login(username, password);
      const sesion = await api.fetchSession();
      guardarSesion(sesion);
      set({ ...aplicarSesion(sesion), signingIn: false });
      return true;
    } catch (error) {
      api.clearSession();
      set({
        ...ESTADO_ANONIMO,
        signingIn: false,
        error:
          error instanceof ApiError && error.status === 401
            ? 'Usuario o contrasena incorrectos.'
            : error instanceof Error
              ? error.message
              : 'No se pudo iniciar sesión.',
      });
      return false;
    }
  },

  signOut: () => {
    api.logout();
    // Lo que se guardó en el navegador para que las pantallas abran rápido no
    // se queda para la próxima persona que use esta computadora.
    olvidarMetaGuardada();
    set({ ...ESTADO_ANONIMO, error: null, signingIn: false });
  },

  setActiveCompany: async (companyId) => {
    const { companies, activeCompanyId } = get();
    if (companyId === activeCompanyId) return true;
    // Cambiar a una company que el backend no habilito seria un 403 en la
    // siguiente peticion; se corta antes y sin tocar el estado.
    if (!companies.some((company) => company.id === companyId)) return false;

    const anterior = activeCompanyId;
    api.setActiveCompanyId(companyId);
    set({ activeCompanyId: companyId });

    try {
      const sesion = await api.fetchSession();
      guardarSesion(sesion);
      set(aplicarSesion(sesion));
      return true;
    } catch (error) {
      // Se revierte para no dejar la UI mostrando una company que el backend no
      // acepto: seria una mentira sobre que datos se estan viendo.
      api.setActiveCompanyId(anterior);
      set({
        activeCompanyId: anterior,
        error: error instanceof Error ? error.message : 'No se pudo cambiar de empresa.',
      });
      return false;
    }
  },

  reload: async () => {
    if (!api.isAuthenticated()) {
      set({ ...ESTADO_ANONIMO });
      return;
    }
    try {
      const sesion = await api.fetchSession();
      guardarSesion(sesion);
      set(aplicarSesion(sesion));
    } catch (error) {
      if (error instanceof ApiError && error.isAuthError) {
        olvidarMetaGuardada();
        set({ ...ESTADO_ANONIMO });
        return;
      }
      set({ error: error instanceof Error ? error.message : 'No se pudo refrescar la sesión.' });
    }
  },
}));

// Si el refresco de token falla en cualquier peticion, la app vuelve al login sin
// que el componente que la disparo tenga que enterarse.
api.onSessionExpired(() => {
  // Lo guardado para dibujar rápido no sobrevive a una sesión vencida.
  olvidarMetaGuardada();
  useSessionStore.setState({ ...ESTADO_ANONIMO, error: 'Tu sesión expiró. Volvé a iniciar sesión.' });
});

// ---------------------------------------------------------------------------
// Selectores
// ---------------------------------------------------------------------------
export const selectUser = (estado: SessionState): SessionUser | null => estado.user;
export const selectStatus = (estado: SessionState): SessionStatus => estado.status;
export const selectCompanies = (estado: SessionState): CompanyRef[] => estado.companies;

export const selectActiveCompany = (estado: SessionState): CompanyRef | null => {
  if (estado.activeCompanyId === null) return null;
  return estado.companies.find((company) => company.id === estado.activeCompanyId) ?? null;
};

/*
 * Aca vivian `ROLES_DE_AFUERA` y `esUsuarioDeAfuera`, que decidian que una
 * persona con el unico rol `consorcios.propietario` aterrizara en `/portal` en
 * vez de en el ERP.
 *
 * Se fueron con el portal del propietario, que se fue con la funcionalidad de
 * expensas: sin liquidaciones no habia nada que mostrarle a un propietario, y
 * el rol dejo de existir. Sin la funcion, quien entre aterriza siempre en el
 * ERP — que hoy es lo correcto, porque todos los que entran son del equipo.
 *
 * Si algun dia vuelve a haber un rol «de afuera» (un consejo de administracion,
 * por ejemplo), esto se reescribe: la lista existia justamente para eso.
 */

/**
 * Nombre de pila, para saludar.
 *
 * El nombre visible de una persona no siempre viene «Nombre Apellido»: el padrón
 * admite el orden de padrón —«Gómez, María Elena»— y ahí cortar por el primer
 * espacio devuelve «Gómez,», que saluda por el apellido y encima deja la coma
 * colgando («Hola, Gómez,»). La regla es una sola y no adivina más de la cuenta:
 *
 *  - si hay una coma, el nombre de pila es lo que va DESPUÉS de ella
 *    («Gómez, María Elena» → «María»);
 *  - si no hay coma, es la primera palabra («Verónica Robledo» → «Verónica»);
 *  - la puntuación del borde se saca siempre, así que nunca queda una coma suelta.
 *
 * Si no se puede deducir nada devuelve la cadena vacía y quien llama decide: el
 * inicio, por ejemplo, titula «Inicio» en vez de saludar a nadie.
 */
export function firstName(user: SessionUser | null): string {
  if (user === null) return '';
  const completo = user.name.trim();
  if (completo === '') return '';

  const coma = completo.indexOf(',');
  // Con coma se prueba primero la parte de la derecha; si está vacía («Gómez,»)
  // se cae a la izquierda, que es lo único que hay.
  const candidatos = coma === -1 ? [completo] : [completo.slice(coma + 1), completo.slice(0, coma)];

  for (const candidato of candidatos) {
    const palabra = candidato.trim().split(/\s+/)[0] ?? '';
    const limpio = palabra.replace(/^[,;.\s]+|[,;.\s]+$/gu, '');
    if (limpio !== '') return limpio;
  }
  return '';
}

/** Iniciales para el avatar. Sin foto de perfil: no la pedimos y no la guardamos. */
export function userInitials(user: SessionUser | null): string {
  if (user === null) return '?';
  const base = user.name.trim() === '' ? user.username : user.name;
  const partes = base.split(/\s+/).filter((parte) => parte !== '');
  const primera = partes[0]?.[0] ?? '?';
  const segunda = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return `${primera}${segunda}`.toUpperCase();
}
